import React, { useState, useEffect, useCallback } from 'react';
import { getCompanies, getCompanySummary } from '../services/api';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('2025-12');

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (err) {
      setError('Failed to load companies');
      console.error(err);
    }
  }, []);

  const fetchSummaries = useCallback(async () => {
    try {
      setLoading(true);
      const summaryData = {};
      
      for (const company of companies) {
        const response = await getCompanySummary(company.id, selectedMonth);
        summaryData[company.id] = response.data;
      }
      
      setSummaries(summaryData);
    } catch (err) {
      setError('Failed to load company summaries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [companies, selectedMonth]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    if (companies.length > 0) {
      fetchSummaries();
    }
  }, [companies, fetchSummaries]);

  if (loading && companies.length === 0) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div>
      <div className="filters">
        <div className="filter-group">
          <label>Month:</label>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            <option value="2025-12">December 2025</option>
            <option value="2025-11">November 2025</option>
            <option value="2025-10">October 2025</option>
          </select>
        </div>
      </div>

      <div className="company-grid">
        {companies.map((company) => {
          const summary = summaries[company.id] || {};
          const totalCredit = parseFloat(summary.total_credit) || 0;
          const totalDebit = parseFloat(summary.total_debit) || 0;
          const netIncome = parseFloat(summary.net_income) || 0;
          const fdInterest = parseFloat(summary.fd_interest) || 0;
          const internalCredit = parseFloat(summary.internal_credit) || 0;
          const internalDebit = parseFloat(summary.internal_debit) || 0;
          const transactionCount = parseInt(summary.transaction_count) || 0;

          return (
            <div key={company.id} className="company-card">
              <div className="company-name">
                {company.name}
                <span className="company-code">{company.code}</span>
              </div>
              
              <div className="company-stats">
                <div className="company-stat-row">
                  <span className="company-stat-label">Total Income</span>
                  <span className="company-stat-value amount-credit">
                    {formatCurrency(totalCredit)}
                  </span>
                </div>
                
                <div className="company-stat-row">
                  <span className="company-stat-label">Total Expenses</span>
                  <span className="company-stat-value amount-debit">
                    {formatCurrency(totalDebit)}
                  </span>
                </div>
                
                <div className="company-stat-row">
                  <span className="company-stat-label">Net Income</span>
                  <span className={`company-stat-value ${netIncome >= 0 ? 'amount-credit' : 'amount-debit'}`}>
                    {formatCurrency(netIncome)}
                  </span>
                </div>
                
                <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid var(--border-color)' }} />
                
                <div className="company-stat-row">
                  <span className="company-stat-label">FD Interest (Passive)</span>
                  <span className="company-stat-value">
                    {formatCurrency(fdInterest)}
                  </span>
                </div>
                
                <div className="company-stat-row">
                  <span className="company-stat-label">Internal Transfers In</span>
                  <span className="company-stat-value">
                    {formatCurrency(internalCredit)}
                  </span>
                </div>
                
                <div className="company-stat-row">
                  <span className="company-stat-label">Internal Transfers Out</span>
                  <span className="company-stat-value">
                    {formatCurrency(internalDebit)}
                  </span>
                </div>
                
                <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid var(--border-color)' }} />
                
                <div className="company-stat-row">
                  <span className="company-stat-label">Transactions</span>
                  <span className="company-stat-value">
                    {transactionCount}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Companies;
